/**
 * ============================================================================
 * ProgramSelectorBanner — Model (form values + RHF resolver + quiz consts)
 * ============================================================================
 * Explicit resolver replaces manual errors state. No Yup.
 * Field names match pre-MVC: fullName / mobile / email / consent.
 * ============================================================================
 */

import type { FieldErrors, Resolver } from "react-hook-form";

export type ProgramSelectorStep = "questions" | "form" | "results";

export type QuestionOption = { id: string; label: string };
export type Question = { id: string; title: string; options: QuestionOption[] };

export interface ProgramSelectorFormValues {
  fullName: string;
  countryCode: string;
  mobile: string;
  email: string;
  consent: boolean;
}

export interface ProgramSelectorBannerProps {
  data?: any[];
  associatedPrograms?: any[];
  associatedCertification?: any[];
}

export const NATIONAL_CODE = "+91";
export const RESEND_SECONDS = 30;

export const STREAMS = [
  { label: "Arts & Humanities", icon: "/_a/icons/arts_and_hum.svg" },
  { label: "Management", icon: "/_a/icons/management.svg" },
  { label: "IT", icon: "/_a/icons/IT.svg" },
  { label: "Commerce", icon: "/_a/icons/commerce.svg" },
  { label: "Artificial Intelligence", icon: "/_a/icons/artificial.svg" },
  { label: "Machine Learning", icon: "/_a/icons/machine.svg" },
];

export const QUESTIONS: Question[] = [
  {
    id: "EducationLevel",
    title: "What's your highest educational qualification?",
    options: [
      { id: "HighSchool", label: "12th Pass / Appearing" },
      { id: "UG", label: "Graduate" },
      { id: "PG", label: "Post Graduate" },
    ],
  },
  {
    id: "InterestIndustry",
    title: "Which stream interests you the most?",
    options: [
      { id: "Business_and_Management", label: "Business and Management" },
      { id: "Tech_and_IT", label: "Tech & IT" },
      { id: "Arts_and_Humanities", label: "Arts & Humanities" },
      { id: "Commerce_and_Finance", label: "Commerce & Finance" },
      { id: "Media_and_Communication", label: "Media & Communication" },
      { id: "AI", label: "AI" },
      { id: "HealthCare", label: "Healthcare" },
      { id: "Travel_and_Tourism", label: "Travel & Tourism" },
      { id: "Data_and_BusinessAnalytics", label: "Data & Business Analytics" },
    ],
  },
  {
    id: "MainGoal",
    title: "What's your primary career goal?",
    options: [
      { id: "Start_my_career_strong", label: "Start My Career Strong" },
      {
        id: "Become_the_First_Graduate_in_My_Family",
        label: "Become the First Graduate in My Family ",
      },
      {
        id: "Graduate_from_a_Global_University",
        label: "Graduate from a Global University",
      },
      {
        id: "Balance_My_Degree_with_Other_Pursuits",
        label: "Balance My Degree with Other Pursuits ",
      },
      {
        id: "Build_In_Demand_Skills_Along_with_a_Degree",
        label: "Build In-Demand Skills Along with a Degree ",
      },
    ],
  },
];

export const RESULT_HIGHLIGHTS = [
  "Learn skills that actually get you hired",
  "Internships + placement support to kickstart your career",
  "Work on real projects before your first job",
  "Mentors to guide you at every step",
];

export const DEFAULT_FORM_VALUES: ProgramSelectorFormValues = {
  fullName: "",
  countryCode: NATIONAL_CODE,
  mobile: "",
  email: "",
  consent: true,
};

export const ERROR_MESSAGES = {
  fullName: "Please enter a valid full name",
  mobile: "Please enter a valid phone number",
  email: "Please enter a valid email address",
  consent: "Please accept terms & conditions",
};

/** Match pre-MVC ProgramSelectorBanner REGEX. */
export const REGEX = {
  fullName: /^[a-zA-Z][a-zA-Z ]{2,49}$/,
  mobileIndia: /^[1-9][0-9]{9}$/,
  mobileIntl: /^[1-9][0-9]{6,14}$/,
  email:
    /^(?!.*\.\.)[a-zA-Z0-9](\.?[a-zA-Z0-9_-]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/,
};

/**
 * Explicit RHF resolver — matches pre-MVC validateField / isFormValid.
 */
export function createProgramSelectorFormResolver(): Resolver<ProgramSelectorFormValues> {
  return (values) => {
    const errors: FieldErrors<ProgramSelectorFormValues> = {};

    if (
      !values.fullName ||
      !REGEX.fullName.test(String(values.fullName).trim())
    ) {
      errors.fullName = {
        type: "validate",
        message: ERROR_MESSAGES.fullName,
      };
    }

    if (!values.email || !REGEX.email.test(String(values.email).trim())) {
      errors.email = {
        type: "validate",
        message: ERROR_MESSAGES.email,
      };
    }

    const mobile = String(values.mobile || "").trim();
    const mobileRe =
      values.countryCode === NATIONAL_CODE ? REGEX.mobileIndia : REGEX.mobileIntl;
    if (!mobile || !mobileRe.test(mobile)) {
      errors.mobile = {
        type: "validate",
        message: ERROR_MESSAGES.mobile,
      };
    }

    if (values.consent !== true) {
      errors.consent = {
        type: "validate",
        message: ERROR_MESSAGES.consent,
      };
    }

    const hasErrors = Object.keys(errors).length > 0;
    return hasErrors
      ? { values: {} as Record<string, never>, errors }
      : { values, errors: {} };
  };
}

/** True when Indian mobile starts with 23 — OTP UI shown but verify skipped. */
export function shouldSkipOtpForMobile(mobile: string): boolean {
  return String(mobile || "").startsWith("23");
}
